
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiDollarSign,
  FiUserPlus,
  FiCheck,
  FiAlertCircle,
  FiRefreshCw,
  FiFileText,
  FiCreditCard,
  FiTrendingUp,
  FiGlobe,
  FiActivity,
  FiInfo,
  FiShield
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { API_URL } from "../../Constants";
import { getAccessToken } from "../../token";
import { toast } from "react-toastify";

const Balance = () => {
  const token = getAccessToken();

  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState(false);

  // DLT States
  const [dltRates, setDltRates] = useState({}); // Stores the object like { INR: 0.025, USD: 1 }
  const [newRate, setNewRate] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [dltLoading, setDltLoading] = useState(false);

  // Form states
  const [selfTopupAmount, setSelfTopupAmount] = useState("");

  // User role & ID
  const [userRole, setUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");

  // Messages
  const [message, setMessage] = useState({ type: "", text: "" });

  // Predefined amounts for quick selection
  const predefinedAmounts = [100, 1000, 10000, 100000, 1000000];

  // Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/v1/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Backend sends liveBalancePaise, converting to Rupees for display
      const balanceInPaisa = response.data?.data?.liveBalancePaise || 0;
      setDashboardStats({
        availableCredits: balanceInPaisa / 100000
      });

    } catch (error) {
      console.error("Error fetching balance:", error);
      setMessage({ type: "error", text: "Failed to load balance" });
    } finally {
      setLoading(false);
    }
  };

  // Fetch DLT Rates
  const fetchDltRates = async () => {
    setDltLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/admin/system/dlt-charge`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDltRates(response.data?.data || {});
    } catch (error) {
      console.error("Error fetching DLT rates:", error);
    } finally {
      setDltLoading(false);
    }
  };

  // Get user role and ID from token
  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        setUserRole(payload.accountType || payload.role || "");
        setCurrentUserId(payload.userId || payload.id || "");
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardStats();
    fetchDltRates();
  }, []);

  // Self Top-up function - INTEGRATED NEW API
  const handleSelfTopup = async () => {
    if (!selfTopupAmount || isNaN(selfTopupAmount) || Number(selfTopupAmount) <= 0) {
      setMessage({ type: "error", text: "Please enter a valid amount" });
      return;
    }

    setTopupLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = getToken();
      // Converting Rupees to Paisa for the backend
      const amountPaise = Number(selfTopupAmount) * 100;

      await axios.post(
        `${API_URL}/api/v1/wallet/admin/ssa-topup`,
        { 
            targetUserId: currentUserId,
            amountPaise: amountPaise 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setMessage({
        type: "success",
        text: `Successfully added ₹${selfTopupAmount} to your wallet!`
      });
      setSelfTopupAmount("");
      fetchDashboardStats();
    } catch (error) {
      console.error("Error in ssa top-up:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to top-up balance"
      });
    } finally {
      setTopupLoading(false);
    }
  };

  // Handle predefined amount selection
  const handleAmountClick = (amount) => {
    setSelfTopupAmount(amount.toString());
  };

  // Update DLT Charge
  const updateDltCharge = async () => {
    if (!newRate) {
      toast.error("Please enter new rate");
      return;
    }
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/system/dlt-charge`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currency: currency,
          amount: Number(newRate),
        }),
      });

      const result = await res.json();

      if (res.ok) {
        toast.success("DLT Charge Updated Successfully");
        setNewRate("");
        fetchDltRates();
      } else {
        toast.error(result.message || "Update failed");
      }
    } catch (error) {
      console.error("Update Error:", error);
      toast.error("Server Error");
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format amount for display
  const formatAmountDisplay = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(0)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount;
  };

  const inputClassName = "w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#0C4550] focus:ring-2 focus:ring-[#0C4550]/10 transition-all duration-300 hover:border-[#0C4550]/30 shadow-sm";
  const labelClassName = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-[#F0EFEB] font-sans p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* --- HEADER SECTION --- */}
        <div className="bg-[#0C4550] rounded-2xl shadow-xl mb-6 px-5 py-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-[#C5F637]/10 to-transparent pointer-events-none" />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between relative z-10">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
                <FiDollarSign className="h-8 w-8 text-[#C5F637]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">Balance & Configuration</h1>
                <p className="text-[#A7A08F] mt-1">Manage wallet funds and global system rates</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3  min-w-[220px] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 text-[#C5F637]">
                  <FiCreditCard className="h-4 w-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Available Credits</span>
                </div>
                <button
                  onClick={fetchDashboardStats}
                  className="text-[#C5F637]/70 hover:text-[#C5F637] transition-colors"
                  title="Refresh Balance"
                >
                  <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
              <div className="flex items-end gap-2">
                {loading ? (
                  <div className="h-8 w-32 bg-white/20 rounded animate-pulse"></div>
                ) : (
                  <div className="text-3xl font-bold text-white tracking-tight">
                    {formatCurrency(dashboardStats?.availableCredits || 0)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- STATUS MESSAGES --- */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl shadow-sm border flex items-center gap-3 animate-in fade-in slide-in-from-top-2 ${
            message.type === 'success'
              ? 'bg-[#C5F637]/10 border-[#C5F637]/20 text-[#0C4550]'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {message.type === 'success' ? 
              <FiCheck size={20} className="text-[#0C4550]" /> : 
              <FiAlertCircle size={20} />
            }
            <span className="font-semibold">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 1. SELF TOP-UP CARD */}
          {userRole === "SUPER_SUPER_ADMIN" && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full">
              <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-[#0C4550]/5 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0C4550]/10 flex items-center justify-center">
                    <FiShield className="h-5 w-5 text-[#0C4550]" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">Admin Top-up</p>
                    <p className="text-xs text-gray-500 font-medium">Add funds to master wallet</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  <div className="bg-[#C5F637]/10 rounded-xl p-4 border border-[#C5F637]/20 flex items-start gap-3">
                    <FiInfo className="text-[#0C4550] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#0C4550] leading-relaxed">
                      Funds added here are credited immediately to the <strong className="text-[#0C4550]">Super Admin</strong> wallet and can be distributed to resellers or users.
                    </p>
                  </div>

                  <div>
                    <label className={`${labelClassName} flex items-center text-gray-600`}>
                      <FaRupeeSign className="mr-2 h-4 w-4 text-[#0C4550]" />
                      Quick Select Amount
                    </label>
                    <div className="grid grid-cols-5 gap-3">
                      {predefinedAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => handleAmountClick(amount)}
                          className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-300 ${
                            selfTopupAmount === amount.toString()
                              ? 'border-[#0C4550] bg-[#0C4550]/5 text-[#0C4550] shadow-sm'
                              : 'border-gray-200 bg-white hover:border-[#0C4550]/30 hover:bg-[#0C4550]/3 hover:shadow-sm'
                          }`}
                        >
                          <span className="text-lg font-bold">
                            <FaRupeeSign className="inline h-3 w-3 mb-0.5" /> {formatAmountDisplay(amount)}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            {amount.toLocaleString('en-IN')}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 items-end">
                    <div className="flex-1">
                      <label className={`${labelClassName} flex items-center`}>
                        <FaRupeeSign className="mr-2 h-4 w-4 text-[#0C4550]" />
                        Top-up Amount (₹)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                          <FaRupeeSign size={18} />
                        </div>
                        <input
                          type="number"
                          value={selfTopupAmount}
                          onChange={(e) => setSelfTopupAmount(e.target.value)}
                          placeholder="Enter custom amount"
                          className={`${inputClassName} pl-11 text-lg font-medium`}
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="sm:w-auto w-full">
                      <button
                        onClick={handleSelfTopup}
                        disabled={topupLoading}
                        className={`w-full sm:w-auto py-3 px-6 rounded-xl font-bold text-white shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                          topupLoading
                            ? 'bg-gray-300 cursor-not-allowed shadow-none'
                            : 'bg-gradient-to-r from-[#0C4550] to-[#0A3A44] hover:from-[#0C4550] hover:to-[#0C4550]/90 hover:shadow-xl active:scale-95'
                        }`}
                      >
                        {topupLoading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FiCheck size={20} /> Confirm
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 2. DLT CONFIGURATION CARD */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full">
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-[#C5F637]/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C5F637]/20 flex items-center justify-center">
                  <FiActivity className="h-5 w-5 text-[#0C4550]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">DLT Rate Configuration</p>
                  <p className="text-xs text-gray-600 font-medium">Manage SMS Compliance Charges</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3 block">
                  Current Rates
                </label>
                {dltLoading ? (
                  <div className="flex gap-3">
                    <div className="h-12 w-24 bg-gray-100 rounded-lg animate-pulse"></div>
                    <div className="h-12 w-24 bg-gray-100 rounded-lg animate-pulse"></div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {Object.keys(dltRates).length > 0 ? (
                      Object.entries(dltRates).map(([curr, rate]) => (
                        <div 
                          key={curr} 
                          className="flex flex-col items-center justify-center p-4 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-[#0C4550]/30 hover:shadow-md transition-all min-w-[90px] group"
                        >
                          <span className="text-xs font-bold text-gray-500 mb-1 group-hover:text-[#0C4550] transition-colors">
                            {curr}
                          </span>
                          <span className="text-base font-bold text-[#0C4550]">
                            {curr === 'INR' ? '₹' : curr === 'USD' ? '$' : curr === 'EUR' ? '€' : curr === 'GBP' ? '£' : curr === 'AED' ? 'د.إ' : ''} {rate}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-400 text-sm py-2 w-full">No rates configured</div>
                    )}
                  </div>
                )}
              </div>

              <div className="h-px bg-gray-100"></div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">
                  Update Rates
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <label className={`${labelClassName} flex items-center`}>
                      <FiGlobe className="mr-2 h-4 w-4 text-[#0C4550]" />
                      Currency
                    </label>
                    <div className="relative">
                      <select
                        className={`${inputClassName} py-3 appearance-none`}
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="AED">AED</option>
                      </select>
                      <FiGlobe className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className={`${labelClassName} flex items-center`}>
                      <FiTrendingUp className="mr-2 h-4 w-4 text-[#0C4550]" />
                      New Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        placeholder="0.05"
                        min="0.0001"
                        step="0.0001"
                        className={inputClassName}
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                      />
                      <FiActivity className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={updateDltCharge}
                    className="w-full py-4 rounded-xl font-bold text-white shadow-lg bg-gradient-to-r from-[#0C4550] to-[#0A3A44] hover:from-[#0C4550] hover:to-[#0C4550]/90 hover:shadow-xl active:scale-95 transition-all duration-300"
                  >
                    Update Configuration
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Information Card */}
        <div className="mt-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0C4550]/10 rounded-xl flex items-center justify-center">
                <FiInfo className="h-6 w-6 text-[#0C4550]" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-800">Balance Management Guide</p>
                <p className="text-gray-500 text-sm">Important information about wallet and rates</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#0C4550]/5 rounded-xl p-4 border border-[#0C4550]/10">
                <h4 className="font-semibold text-[#0C4550] mb-3 flex items-center gap-2">
                  <FiCheck className="h-4 w-4" />
                  Wallet Credits
                </h4>
                <ul className="space-y-2 text-sm text-[#0C4550]/80">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0C4550] rounded-full"></div>
                    Credits are used for sending SMS messages
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0C4550] rounded-full"></div>
                    Balance is shown in Indian Rupees (₹)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0C4550] rounded-full"></div>
                    Top-up required when balance is low
                  </li>
                </ul>
              </div>

              <div className="bg-[#C5F637]/10 rounded-xl p-4 border border-[#C5F637]/20">
                <h4 className="font-semibold text-[#0C4550] mb-3 flex items-center gap-2">
                  <FiFileText className="h-4 w-4" />
                  DLT Charges
                </h4>
                <ul className="space-y-2 text-sm text-[#0C4550]/70">
                  <li>• DLT charges are per-SMS compliance fees</li>
                  <li>• Rates vary by currency and region</li>
                  <li>• Configure rates for all supported currencies</li>
                  <li>• Updates apply immediately to new transactions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Balance;